// Globals
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { addOrderComment, getOrderDetails, ordersSelector, updateOrderProducts } from '../../feature';
import { selectAdminProfile } from '@/modules/AdminProfile';

// Modules
import { usePermissions, Resource } from '@/modules/Permissions';

// Models
import { IOrderProduct, OrdersDeliveryType } from '@/models/orders.model';

// Components
import { showErrorToast, showSuccessToast, useForm } from '@/components';
import {
  StyledContainerWrapper,
  StyledContainerHeader,
  StyledProductsCountText,
  StyledHeaderButtonsWrapper,
  StyledForm,
  StyledAddProductButton,
  StyledAddProductIconWrapper,
  StyledDrawer,
} from './OrderProductsContainerStyles';
import { Comments, CompositionChangeReasonModal, OrderProductsList, SummaryWeightAndPrice } from '../../components';
import { AddProductContainer } from '../../containers';
import { Button, Skeleton } from '@/ui';
import { CloseIcon } from '@/icons';

// Helpers
import {
  OrderDetailsFormFieldsNames,
  TOrderDetailsFormValues,
  getProductsFormValues,
  hasUnsavedOrderComments,
  isOrderContentEditable,
  requiresCompositionChangeReason,
  resetOrderCommentFormValues,
  syncOrderProductsFormValues,
} from '../../helpers';
import { showApiErrors } from '@/utils';
import { UpdateOrdersProductsDTO } from '../../dtos';
import { useBoolean, useModal } from '@/hooks';

const OrderProductsContainer = () => {
  const { t } = useTranslation(['orders']);
  const dispatch = useAppDispatch();
  const [form] = useForm<TOrderDetailsFormValues>();
  const { order, isLoading, isUpdatingProducts } = useAppState(ordersSelector);
  const profile = useAppState(selectAdminProfile);
  const { canManage } = usePermissions();
  const canManageOrders = canManage(Resource.ORDER);
  const initialValues: TOrderDetailsFormValues | undefined = getProductsFormValues(order);
  const deliveryType = order?.delivery?.delivery as OrdersDeliveryType | undefined;
  const isEditable = isOrderContentEditable(order?.status, deliveryType, profile?.role) && canManageOrders;

  const {
    value: isOpenAddProductDrawer,
    setFalse: closeAddProductDrawer,
    setTrue: openAddProductDrawer,
  } = useBoolean();
  const { isOpened: isReasonModalOpen, openModal: openReasonModal, closeModal: closeReasonModal } = useModal();
  const [pendingDto, setPendingDto] = useState<UpdateOrdersProductsDTO | null>(null);

  const productsTotalCount = order?.sub_orders.reduce((acc, el) => acc + el.products.length, 0) || 0;
  const alreadyAddedProducts = initialValues?.[OrderDetailsFormFieldsNames.PRODUCTS];
  const wasUpdatingProducts = useRef(false);

  useEffect(() => {
    syncOrderProductsFormValues(form, order);

    if (wasUpdatingProducts.current && !isUpdatingProducts) {
      resetOrderCommentFormValues(form, order);
    }

    wasUpdatingProducts.current = isUpdatingProducts;
  }, [form, order, isUpdatingProducts]);

  const refreshOrderDetails = async (orderId: string) => {
    await dispatch(getOrderDetails(orderId)).unwrap();
  };

  const saveProducts = async (requestDto: UpdateOrdersProductsDTO) => {
    await dispatch(updateOrderProducts(requestDto)).unwrap();
    await refreshOrderDetails(requestDto.order_id);
    showSuccessToast({ message: t('order_data_saved') });
  };

  const handleOpenAddProductDrawer = async () => {
    if (!order?.id) {
      return;
    }

    try {
      await refreshOrderDetails(order.id);
      openAddProductDrawer();
    } catch (error) {
      showApiErrors(error);
    }
  };

  const submitWithOptionalReason = async (requestDto: UpdateOrdersProductsDTO) => {
    if (requiresCompositionChangeReason(order?.status, order?.payment_status)) {
      setPendingDto(requestDto);
      openReasonModal();
      return;
    }

    await saveProducts(requestDto);
  };

  const handleSubmitProducts = async () => {
    try {
      const { products, comments } = form.getFieldsValue();

      if (hasUnsavedOrderComments(comments)) {
        showErrorToast({ message: t('order_unsent_comment_warning') });
        return;
      }

      const requestDto = new UpdateOrdersProductsDTO({ order_id: order?.id as string, products });
      await submitWithOptionalReason(requestDto);
    } catch (error) {
      showApiErrors(error);
    }
  };

  const handleDelete = async (orderProduct: IOrderProduct) => {
    try {
      const { products, comments } = form.getFieldsValue();

      if (hasUnsavedOrderComments(comments)) {
        showErrorToast({ message: t('order_unsent_comment_warning') });
        return;
      }

      const requestDto = new UpdateOrdersProductsDTO({
        order_id: order?.id as string,
        products,
        productToDelete: orderProduct.id,
      });
      await submitWithOptionalReason(requestDto);
    } catch (error) {
      showApiErrors(error);
    }
  };

  const handleConfirmReason = async (reason: string) => {
    if (!pendingDto) {
      return;
    }

    try {
      pendingDto.reason = reason;
      await saveProducts(pendingDto);
      setPendingDto(null);
      closeReasonModal();
    } catch (error) {
      showApiErrors(error);
    }
  };

  const handleSubmitComment = async (subOrderId?: string) => {
    if (order && subOrderId)
      try {
        const { comments } = form.getFieldsValue();
        await dispatch(
          addOrderComment({
            order_id: order?.id,
            subOrder_id: subOrderId,
            comment: comments[subOrderId],
          }),
        ).unwrap();
        form.setFieldValue([OrderDetailsFormFieldsNames.COMMENTS, subOrderId], '');
        showSuccessToast({ message: t('order_data_saved') });
      } catch (error) {}
  };

  if (isLoading && !order) return <Skeleton />;

  return (
    <StyledContainerWrapper>
      <StyledContainerHeader>
        <StyledProductsCountText>{`${t(
          'order_products_list_products',
        )} (${productsTotalCount})`}</StyledProductsCountText>
        {isEditable && (
          <StyledHeaderButtonsWrapper>
            <StyledAddProductButton onClick={handleOpenAddProductDrawer} loading={isUpdatingProducts}>
              <StyledAddProductIconWrapper>
                <CloseIcon />
              </StyledAddProductIconWrapper>
              {t('order_products_list_add_product_btn')}
            </StyledAddProductButton>
            <Button htmlType="submit" type="primary" onClick={handleSubmitProducts} loading={isUpdatingProducts}>
              {t('order_products_list_save_changes_btn')}
            </Button>
          </StyledHeaderButtonsWrapper>
        )}
      </StyledContainerHeader>
      <StyledForm initialValues={initialValues} onFinish={handleSubmitProducts} form={form}>
        {order && <SummaryWeightAndPrice data={order} isSummaryInfo />}
        {order?.sub_orders.map((subOrder) => (
          <React.Fragment key={subOrder.id}>
            <OrderProductsList
              supplierName={subOrder.store_title}
              orderProducts={subOrder.products}
              isLoading={isUpdatingProducts}
              handleDelete={handleDelete}
              form={form}
              isEditable={isEditable}
              isPartnerConfirmed={Boolean(subOrder.is_partner_confirmed)}
              partnerConfirmedByName={subOrder.partner_confirmed_by?.full_name}
              partnerConfirmedAt={subOrder.partner_confirmed_at}
            />
            <SummaryWeightAndPrice data={subOrder} />
            <Comments
              comments={subOrder.comments}
              subOrderId={subOrder.id}
              isLoading={false}
              handleSubmit={handleSubmitComment}
              formInstance={form}
              orderStatus={order?.status}
              canManage={canManageOrders}
            />
          </React.Fragment>
        ))}
      </StyledForm>
      <StyledDrawer open={isOpenAddProductDrawer} onClose={closeAddProductDrawer} size="large" destroyOnClose>
        <AddProductContainer
          closeAddProductDrawer={closeAddProductDrawer}
          alreadyAddedProducts={alreadyAddedProducts}
          productsForm={form}
        />
      </StyledDrawer>
      <CompositionChangeReasonModal
        isOpen={isReasonModalOpen}
        isLoading={isUpdatingProducts}
        onClose={() => {
          setPendingDto(null);
          closeReasonModal();
        }}
        onConfirm={handleConfirmReason}
      />
    </StyledContainerWrapper>
  );
};
export default OrderProductsContainer;
